import {
  addEntities,
  deleteAllEntities,
  deleteEntities,
  getEntity,
  hasEntity,
  selectAllEntities,
  selectEntity,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { createStore } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { z } from '@4players/odin-common/zod';
import { distinctUntilChanged, filter, map, Observable, scan, throttleTime } from 'rxjs';
import { AudioOutput, VideoInput, VideoOutput } from '@4players/odin';
import { WhiteboardElement } from '../shared/components/ng-whiteboard';
import { sortUserList } from '../shared/helpers/user-list';
import { isImageDataUrl } from '../shared/helpers/image-data-url';

export const userStatusSchema = z.union([z.literal('online'), z.literal('away')]).default('online');

export type UserStatus = z.infer<typeof userStatusSchema>;

export const userDataSchema = z.object({
  name: z.string().default('Unknown'),
  status: userStatusSchema.catch('online').default('online'),
  // Custom avatar set by the user (synced across clients via userData). Empty
  // by default — when empty, each client renders a locally generated avatar
  // derived from the user's name (see `resolveAvatar`), so the default avatar
  // is never stored or transmitted. Anything but an image data URL is
  // discarded on its own (`.catch`): failing the whole parse would cost the
  // peer's name and mute state too (see `createUser` / `updateRemoteUserData`).
  avatar: z
    .string()
    .refine((value) => value === '' || isImageDataUrl(value))
    .catch('')
    .default(''),
  outputMuted: z.boolean().catch(false).default(false),
  inputMuted: z.boolean().catch(false).default(false),
  renderer: z.string().default('').optional(),
  platform: z.string().default('').optional(),
  revision: z.string().default('').optional(),
  version: z.string().default('').optional(),
  buildNo: z.number().default(0).optional(),
  role: z.string().default('Guest').optional(),
  statusMessage: z.string().optional(),
  presentationStartedAt: z.number().default(0),
  breakoutRoomId: z.string().default('').optional(),
  hasRaisedHand: z.boolean().default(false).optional(),
  joinedAt: z.number().nullable().default(null),
});

export type UserData = z.infer<typeof userDataSchema>;

export interface User {
  // Stable identity: equals the ODIN token's `userId` claim, which equals
  // the server-issued `andorUUID`. Never re-keyed across the lifetime of
  // the entity.
  id: string;
  // Current ODIN peer id. `null` when the user is not currently connected
  // to a room (e.g. own user before joining, or remote user after PeerLeft
  // when we keep the entity around to preserve message history). Mutates
  // on each (re)join.
  peerId: number | null;
  data: UserData;
  isSelf: boolean;
  isActiveSpeaker: boolean;
  isFavorite: boolean;
  coordinates: UserCoordinates;
  screenCastStream?: VideoInput | VideoOutput;
  camStream?: VideoInput | VideoOutput;
  whiteboard?: WhiteboardElement[];
  audioOutput?: AudioOutput;
}

export interface UserCoordinates {
  x: number;
  y: number;
  z: number;
}

export type BreakoutRoomChange = {
  user: User;
  previousBreakoutRoomId?: string;
};

// Dummy users created by the dev console (see `UserService.createDummyUsers`)
// have ids prefixed with `dummy-` and intentionally carry `peerId: null` —
// they exist only client-side as UI fixtures and have no ODIN peer.
export const DUMMY_USER_ID_PREFIX = 'dummy-';
export const isDummyUser = (u: User): boolean => u.id.startsWith(DUMMY_USER_ID_PREFIX);

// Who counts as present in the room; see `userListUsers$` for the rules.
const isListedUser = (u: User): boolean => u.isSelf || u.peerId !== null || isDummyUser(u);

const userStore = createStore({ name: 'user' }, withEntities<User>());

@Injectable({
  providedIn: 'root',
})
export class UserStore {
  store = userStore;
  allUsers$ = userStore.pipe(
    selectAllEntities(),
    map((users) => {
      return sortUserList(users);
    }),
    filter((users) => users.length > 0)
  );

  // Drives the side-panel and spatial views. A remote peer that has left
  // is kept in the store (entity preserved so chat history can resolve
  // their name/avatar via `_userIdToName`) but must not surface as an
  // interactive row — `_onPeerLeft` signals that by nulling their peerId,
  // and the `isListedUser` peerId check filters them out. The `isSelf`
  // exemption covers a window where the own user is briefly in the store
  // without a peerId: on `persistAppState` reload the conference panel
  // mounts before `OdinService._onRoomJoined` calls `setPeerId`, and
  // during reconnect the media teardown re-resets the own peerId to null
  // until `_onRoomJoined` fires again. Dummies are dev fixtures with
  // `peerId: null` by design and are exempted explicitly; the broadcast
  // concern they would otherwise raise is handled by the null-target
  // guard in `OdinService.sendMessage`.
  userListUsers$ = this.store.pipe(
    throttleTime(200, undefined, { leading: true, trailing: true }),
    selectAllEntities(),
    map((users) => sortUserList(users.filter(isListedUser)))
  );

  /** Number of users `userListUsers$` lists, without sorting them. */
  userCount$ = this.store.pipe(
    throttleTime(200, undefined, { leading: true, trailing: true }),
    selectAllEntities(),
    map((users) => users.filter(isListedUser).length),
    distinctUntilChanged()
  );

  /**
   * Emits only the users whose breakoutRoomId changed.
   * Each entry carries the previous breakoutRoomId (`undefined` when the user
   * is new to the store, i.e. first appearance — not a real room change).
   */
  breakoutRoomChanges$ = this.store.pipe(
    selectAllEntities(),
    scan(
      (acc, curr) => {
        const prevMap = new Map(acc.prev.map((u) => [u.id, u.data.breakoutRoomId]));

        const changed: BreakoutRoomChange[] = curr
          .filter((u) => !prevMap.has(u.id) || prevMap.get(u.id) !== u.data.breakoutRoomId)
          .map((u) => ({
            user: u,
            previousBreakoutRoomId: prevMap.has(u.id) ? prevMap.get(u.id)! : undefined,
          }));
        return { prev: curr, changed };
      },
      { prev: [] as User[], changed: [] as BreakoutRoomChange[] }
    ),
    map((result) => result.changed),
    filter((changed) => changed.length > 0)
  );

  /**
   * Emits users whose hasRaisedHand changed from false/undefined to true.
   * Each client watches this locally to generate synthetic chat messages.
   */
  raisedHandChanges$ = this.store.pipe(
    selectAllEntities(),
    scan(
      (acc, curr) => {
        const prevMap = new Map(acc.prev.map((u) => [u.id, !!u.data.hasRaisedHand]));
        const raised = curr.filter((u) => !!u.data.hasRaisedHand && !prevMap.get(u.id));
        return { prev: curr, raised };
      },
      { prev: [] as User[], raised: [] as User[] }
    ),
    map((result) => result.raised),
    filter((raised) => raised.length > 0)
  );

  /**
   * Emits users whose hasRaisedHand changed from true to false/undefined.
   */
  loweredHandChanges$ = this.store.pipe(
    selectAllEntities(),
    scan(
      (acc, curr) => {
        const currMap = new Map(curr.map((u) => [u.id, !!u.data.hasRaisedHand]));
        const lowered = acc.prev.filter((u) => !!u.data.hasRaisedHand && !currMap.get(u.id));
        return { prev: curr, lowered };
      },
      { prev: [] as User[], lowered: [] as User[] }
    ),
    map((result) => result.lowered),
    filter((lowered) => lowered.length > 0)
  );

  // Same departed-peer filter as `userListUsers$`: once `_onPeerLeft` nulls
  // a peer's peerId the entity stays in the store for chat-history name
  // resolution, but it must not surface as an interactive tile here. The
  // `isSelf` and dummy exemptions exist for the reasons documented above
  // `userListUsers$`.
  videoListUsers$ = this.store.pipe(
    throttleTime(200, undefined, { leading: true, trailing: true }),
    selectAllEntities(),
    map((users) => users.filter(isListedUser))
  );

  get$(id: string): Observable<User | undefined> {
    return this.store.pipe(selectEntity(id));
  }

  getUserData$(id: string) {
    return this.store.pipe(selectEntity(id, { pluck: 'data' }));
  }

  add(user: User): void {
    this.store.update(addEntities(user));
  }
  get(id: string): User | undefined {
    return this.store.query(getEntity(id));
  }
  getCamFromUser$(userId: string): Observable<VideoInput | undefined> {
    return this.store.pipe(
      selectEntity(userId, { pluck: 'camStream' }),
      map((video) => {
        if (video && video.kind === 'video-output') return undefined;
        return video;
      })
    );
  }

  getScreenCastFromUser$(userId: string): Observable<VideoInput | undefined> {
    return this.store.pipe(
      selectEntity(userId, { pluck: 'screenCastStream' }),
      map((video) => {
        if (video && video.kind === 'video-output') return undefined;
        return video;
      })
    );
  }

  getAudioOutputFromUser$(userId: string): Observable<AudioOutput | undefined> {
    return this.store.pipe(selectEntity(userId, { pluck: 'audioOutput' }));
  }
  has(id: string): boolean {
    return this.store.query(hasEntity(id));
  }
  update(user: User) {
    this.store.update(updateEntities(user.id, () => user));
  }

  setPeerId(id: string, peerId: number | null) {
    this.store.update(updateEntities(id, (u) => ({ ...u, peerId })));
  }

  deleteAll(): void {
    this.store.update(deleteAllEntities());
  }

  delete(id: string): void {
    this.store.update(deleteEntities(id));
  }
}
